import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { executeSimulation } from '../../core/use-cases/executeSimulation'
import {
  InsuranceUnit,
  selectSimulation,
} from '../../redux/reducers/simulation.reducer'
import { useAppDispatch, useAppSelector } from '../../redux/store'
import * as S from '../styles'
import { Select } from '../ui/form/Select'
import { mq } from '../breakpoints'
import { HelpButton } from '../ui/HelpButton'
import { selectSelectedCareById } from '../../redux/selectors'
import { retrieveLexicalCodes } from '../../core/use-cases/retrieveLexicalCodes'
import { CommonCare } from '../../core/models/CommonCare'
import { CcamCode } from '../../core/models/CcamCode'
import { selectLexicalValues } from '../../redux/reducers/lexicalCodes.reducer'

const ssRateFor = (
  care: CommonCare | CcamCode,
  aldChecked: boolean,
  amChecked: boolean
) => {
  if (!care) {
    return null
  }

  if (aldChecked) {
    return Number(care.ssRateAld)
  } else if (amChecked) {
    return Number(care.ssRateAM)
  } else {
    return Number(care.ssRate)
  }
}

const insuranceUnitOptions = {
  brss: { label: '% BRSS (%BR)', value: 'brss' },
  brss_ss: { label: '% BRSS-SS (%BR - BRSS)', value: 'brss_ss' },
}

export const CareInformationSection = () => {
  const dispatch = useAppDispatch()

  const simulation = useAppSelector(selectSimulation)

  const [currentCareAmount, setCurrentCareAmount] = useState<string | null>(
    null
  )
  const [currentInsuranceRate, setCurrentInsuranceRate] = useState<
    number | null
  >(null)
  const [aldChecked, setAldChecked] = useState<boolean>(false)
  const [amChecked, setAMChecked] = useState<boolean>(false)

  const [currentInsuranceUnit, setCurrentInsuranceUnit] =
    useState<InsuranceUnit | null>(null)

  const selectedCare = useAppSelector(
    selectSelectedCareById(simulation.selectedCareId)
  )

  const ssRate = ssRateFor(selectedCare, aldChecked, amChecked)
  const [currentSsRate, setCurrentSsRate] = useState<number | null>(ssRate)
  const lexicalValues = useAppSelector(
    selectLexicalValues(selectedCare?.lexicalCode)
  )
  const [currentSeparator, setCurrentSeparator] = useState(',')

  const formIsValid =
    currentCareAmount !== null &&
    currentSsRate > 0 &&
    selectedCare !== null &&
    currentInsuranceUnit !== null &&
    currentInsuranceRate !== null

  useEffect(() => {
    dispatch(retrieveLexicalCodes())
  }, [])

  useEffect(() => {
    setCurrentSsRate(ssRate)
  }, [ssRate])

  useEffect(() => {
    setCurrentCareAmount(null)
    setCurrentInsuranceRate(null)
    setAMChecked(false)
    setAldChecked(false)
    setCurrentInsuranceUnit(null)
  }, [selectedCare, simulation.type])

  const onSubmit = () => {
    dispatch(
      executeSimulation({
        ssRate: currentSsRate,
        careAmount: Number(currentCareAmount?.replace(',', '.')),
        insuranceRate: currentInsuranceRate,
        insuranceUnit: currentInsuranceUnit,
        referenceAmount: Number(selectedCare.referenceAmount),
      })
    ).unwrap()
  }

  return (
    <Container>
      <ResponsiveRow>
        <Column>
          <S.InputGroup>
            <S.InputLabel>Le montant de votre soin</S.InputLabel>
            <S.SuffixedTextField
              allowNegativeValue={false}
              placeholder='€'
              groupSeparator=' '
              allowDecimals={true}
              decimalsLimit={2}
              suffix='€'
              decimalSeparator={currentSeparator}
              value={currentCareAmount ?? ''}
              onKeyDown={e => {
                if (e.key === '.') {
                  setCurrentSeparator('.')
                } else if (e.key === ',') {
                  setCurrentSeparator(',')
                }
              }}
              onValueChange={setCurrentCareAmount}
            />
          </S.InputGroup>
        </Column>
        <CenteredColumn>
          <S.InputGroup>
            <S.InputLabel>Taux de remboursement sécurité sociale</S.InputLabel>
            <S.SuffixedTextField
              allowNegativeValue={false}
              placeholder='%'
              groupSeparator=' '
              allowDecimals={true}
              decimalsLimit={2}
              suffix='%'
              value={currentSsRate ?? ''}
              onValueChange={value => {
                const amount = Number(value)

                if (amount > 0 && amount <= 100) {
                  setCurrentSsRate(amount)
                } else if (amount > 100) {
                  setCurrentSsRate(100)
                } else {
                  setCurrentSsRate(null)
                }
              }}
            />
          </S.InputGroup>
          <SpecificSituationForm>
            <LightTextCenter>
              Êtes-vous concerné(e) par l’une de ces situations spécifiques ?
            </LightTextCenter>
            <div>
              <CheckBoxGroup>
                <CheckBox
                  id='am-checkbox'
                  type='checkbox'
                  checked={amChecked}
                  onChange={e => setAMChecked(e.target.checked)}
                />
                <label htmlFor='amcheckbox'>
                  <SemiBold>Régime Alsace Moselle</SemiBold>
                </label>
              </CheckBoxGroup>
              <CheckBoxGroup>
                <CheckBox
                  id='ald-checkbox'
                  type='checkbox'
                  checked={aldChecked}
                  onChange={e => setAldChecked(e.target.checked)}
                />
                <label htmlFor='ald-checkbox'>
                  <SemiBold>ALD</SemiBold> (Affection Longue durée)
                </label>
              </CheckBoxGroup>
            </div>
          </SpecificSituationForm>
        </CenteredColumn>
        <Column>
          <WarrantyLevelForm>
            <S.InputLabel>
              Niveau de garantie de votre mutuelle{' '}
              <HelpButton
                content={
                  selectedCare ? (
                    <div>
                      Le niveau de garantie est à récupérer dans le tableau de
                      garantie fourni par votre mutuelle ou votre assureur.
                      D’après les informations que vous avez fournies, la
                      catégorie associée peut mentionner les mots-clés
                      suivants&nbsp;:
                      <ul>
                        {lexicalValues.map((value, index) => (
                          <li key={index}>{value}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div>
                      Le niveau de garantie est à récupérer dans le tableau de
                      garantie fourni par votre mutuelle ou votre assureur.
                      Sélectionnez un type de soin pour que nous puissions vous
                      guider.
                    </div>
                  )
                }
              />
            </S.InputLabel>
            <ResponsiveColumn>
              <S.SuffixedTextField
                allowNegativeValue={false}
                placeholder='%'
                disableGroupSeparators={true}
                allowDecimals={true}
                decimalSeparator='.'
                decimalsLimit={2}
                suffix='%'
                value={currentInsuranceRate ?? ''}
                onValueChange={value =>
                  setCurrentInsuranceRate(
                    Number(value) > 0 ? Number(value) : null
                  )
                }
              />
              <Select
                placeholder=''
                isSearchable={false}
                value={
                  currentInsuranceUnit != null
                    ? insuranceUnitOptions[currentInsuranceUnit]
                    : { label: '', value: '' }
                }
                options={Object.values(insuranceUnitOptions)}
                onChange={option =>
                  setCurrentInsuranceUnit(option.value as InsuranceUnit)
                }
              />
            </ResponsiveColumn>
          </WarrantyLevelForm>
          {currentInsuranceUnit == 'brss' && (
            <InsuranceUnitDisclaimer>
              Vous avez sélectionné{' '}
              {insuranceUnitOptions[currentInsuranceUnit].label}, si vos
              garanties viennent en complément des garanties de la base de
              sécurité sociale, vous devez sélectionner{' '}
              {insuranceUnitOptions['brss_ss'].label}. Cette information peut se
              trouver en dehors du tableau de garanties.
            </InsuranceUnitDisclaimer>
          )}
        </Column>
      </ResponsiveRow>
      <FormSubmit color='#ff41ae' onClick={onSubmit} disabled={!formIsValid}>
        OK
      </FormSubmit>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 30px 10px 50px 10px;
  background-color: white;
  max-width: 1200px;
  margin: 0 auto;
`

const ResponsiveRow = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: 30px;

  ${mq.lg} {
    flex-direction: row;
    column-gap: 20px;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`

const CenteredColumn = styled(Column)`
  ${mq.lg} {
    min-width: 340px;
  }
`

const ResponsiveColumn = styled(Column)`
  row-gap: 10px;
  width: 100%;
  max-width: 360px;

  ${mq.lg} {
    flex-direction: row;
    column-gap: 10px;
  }
`

const LightTextCenter = styled.span`
  display: block;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 300;
  max-width: 320px;
`

const SpecificSituationForm = styled(S.FlexColumn)`
  margin-top: 20px;
  align-items: center;
`

const SemiBold = styled.span`
  font-weight: 600;
`

const CheckBox = styled.input`
  height: 20px;
  width: 20px;
  margin-right: 12px;
`

const CheckBoxGroup = styled.div`
  display: flex;
  align-items: center;

  & + & {
    margin-top: 10px;
  }
`

const WarrantyLevelForm = styled(S.FlexColumnCenter)`
  margin-top: 30px;
  width: 100%;

  ${mq.lg} {
    margin-top: inherit;
  }
`

const FormSubmit = styled(S.PrimaryButton)`
  margin-top: 30px;
`

const InsuranceUnitDisclaimer = styled.div`
  margin-top: 20px;
  color: #ff41ae;
  font-weight: 300;
  text-align: center;
  max-width: 360px;
`
